import { useState } from 'react';
import { Box, Chip, Link } from '@mui/material';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';
import { truncatedChipListSx } from 'common/table/styles/truncatedChipListSx';
import { useTalentListNames } from 'modules/candidate-profile/hooks/useTalentListNames';

type TalentListChipsCellProps = {
  talentListIds: string[];
  candidateProfileListProps: CandidateProfileListProps;
  maxItems?: number;
};

export const TalentListChipsCell: React.FC<TalentListChipsCellProps> = ({
  talentListIds,
  candidateProfileListProps,
  maxItems = 4
}) => {
  const { navigateToPath } = usePathsV2();
  const { names } = useTalentListNames(talentListIds);
  const [showAll, setShowAll] = useState(false);

  // Only lists that resolved are shown. A candidate can sit on lists the viewer
  // can't see, and those must not appear, not even as a raw id while names load.
  const visibleListIds = talentListIds.filter((id) => names.has(id));

  if (visibleListIds.length === 0) return null;

  const visibleItems = showAll
    ? visibleListIds
    : visibleListIds.slice(0, maxItems);
  const remainingCount = visibleListIds.length - maxItems;

  const handleChipClick = (talentListId: string) => {
    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
        navigateToPath.TALENT_POOL_DETAILS({ talentPoolId: talentListId });
        break;
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
        navigateToPath.ANALYSE_TALENT_POOL_DETAILS({
          candidateBatchId: candidateProfileListProps.candidateBatchId,
          talentPoolId: talentListId
        });
        break;
      default:
        // Column is hidden for other types
        break;
    }
  };

  return (
    <Box sx={truncatedChipListSx.container()}>
      {visibleItems.map((talentListId, index) => (
        <Chip
          key={`${talentListId}-${index}`}
          label={names.get(talentListId)}
          size="small"
          color="primary"
          variant="inverse"
          onClick={() => handleChipClick(talentListId)}
          sx={truncatedChipListSx.clickableChip}
        />
      ))}
      {remainingCount > 0 && (
        <Link
          component="button"
          onClick={() => setShowAll(!showAll)}
          sx={truncatedChipListSx.showMoreLink}
        >
          {showAll ? 'See less' : `+${remainingCount} more`}
        </Link>
      )}
    </Box>
  );
};
